import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, from, switchMap, tap, timeout, catchError, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthResponse, DanoralUser, Acceso } from '../models/danoral.models';
import { solveChallenge, sha } from 'altcha/lib';

// Site Key pública de Google reCAPTCHA v3 (segura de exponer en frontend;
// la validación real ocurre server-side contra la Secret Key, que nunca
// se expone aquí).


export const TYPE_LOGIN_PASSWORD  = 0;  // usuario web existe, pedir contraseña
export const TYPE_LOGIN_USUARIO   = 1;  // usuario web autenticado
export const TYPE_LOGIN_CODCLIENT = 2;  // cod-cliente autenticado

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly API       = environment.apiUrl;
  private readonly TOKEN_KEY = 'dw_token';
  private readonly USER_KEY  = 'dw_user';
  private readonly DASHBOARD_KEY = 'dw_dashboard';
  private readonly JUNTA_KEY = 'dw_junta_desbloqueada';
  private cerrandoSesion = false;

  constructor(private http: HttpClient, private router: Router) {}

  // ── Login unificado (igual a app-danoral) ─────────────────────────────
  // Paso 1: solo username → backend responde typeLogin 0, 1 o 2
  // Paso 2: username + password + idUser → login definitivo usuario web
  loginUnificado(credentials: { username: string; password?: string; idUser?: string }): Observable<AuthResponse> {
    return from(this.obtenerTokenRecaptcha()).pipe(
      switchMap(({ token, diag }) =>
        this.http.post<AuthResponse>(`${this.API}/auth/login-unificado`, {
          ...credentials,
          recaptchaToken: token,
          // Solo viaja cuando el token vino vacio - le dice al backend QUE
          // fallo realmente en el navegador (network, crypto, sin solucion,
          // etc.) en vez del "no llego payload" generico que teniamos antes.
          // Temporal, para diagnosticar casos como el de Samsung
          // Browser/CriOS que fallan siempre aunque el fetch del challenge
          // si responda 200.
          ...(token ? {} : { altchaClientError: diag }),
        })
      ),
      tap(res => {
        // Solo guardar sesión si viene token (typeLogin 1 o 2)
        if (res.token) this.guardarSesion(res);
      }),
      // Red de seguridad: si por lo que sea (red, SW, CPU frenado por Modo de
      // Bajo Consumo resolviendo el proof-of-work, lo que sea) el flujo
      // completo de challenge+login no resuelve en 30s, lo cortamos aca en
      // vez de dejar el spinner pegado indefinidamente. 30s (no 15s) a
      // proposito: confirmado con un caso real que el Modo de Bajo Consumo
      // de iOS frena el CPU lo suficiente como para que el calculo del
      // proof-of-work (que normalmente toma ~1-2s) se pase de los 15s
      // originales. No reemplaza investigar un colgado puntual real, pero
      // garantiza que el usuario siempre reciba una respuesta en vez de
      // quedar esperando para siempre.
      timeout(55000),
      catchError(err => {
        if (err?.name === 'TimeoutError') {
          // Fire-and-forget: visibilidad de cuanta gente choca con esto
          // (probablemente CPU frenado por Modo de Bajo Consumo resolviendo
          // el proof-of-work). No bloquea nada si falla.
          fetch(`${this.API}/auth/webview-sin-crypto?motivo=timeout_altcha`).catch(() => {});
          return throwError(() => ({
            error: { message: 'La conexión tardó demasiado. Verifica tu internet e intenta de nuevo.' },
          }));
        }
        return throwError(() => err);
      }),
    );
  }


  // Obtiene un payload ALTCHA: pide un challenge firmado al backend, resuelve el
  // proof-of-work en el navegador (invisible) y devuelve el payload base64 que el
  // backend verifica criptograficamente. Si algo falla, devuelve token='' junto
  // con "diag" (diagnostico corto de que fallo exactamente - fetch, sin
  // solucion, o el nombre/mensaje real de la excepcion) para poder mandarlo
  // al backend y verlo en el log, en vez de perder esa informacion como
  // pasaba antes.
  //
  // Hace hasta 2 intentos: una conexion movil intermitente (comun en Venezuela)
  // puede hacer fallar el fetch del challenge sin que sea un problema real de
  // seguridad; un solo reintento cubre la mayoria de esos casos sin demorar
  // demasiado el login si de verdad hay un problema persistente.
  private async obtenerTokenRecaptcha(): Promise<{ token: string; diag: string }> {
    const intento1 = await this.intentarResolverAltcha();
    if (intento1.token) return intento1;

    // Pequeña pausa antes de reintentar: si el primer intento fallo por un
    // tropiezo momentaneo de red (comun en conexiones moviles inestables),
    // repetir de inmediato tiende a repetir el mismo fallo. Un segundo de
    // por medio le da chance real a que la conexion se recupere.
    await new Promise(resolve => setTimeout(resolve, 1000));

    const intento2 = await this.intentarResolverAltcha();
    return intento2;
  }

  // Prefetch del desafio de Altcha: login.component.ts lo dispara apenas se
  // monta la pantalla de login (mientras la persona escribe su codigo), asi
  // cuando presiona "Iniciar sesion" ya no hace falta ese primer viaje de
  // red - se ahorra un tramo completo de latencia en el camino critico.
  // Se consume una sola vez; si la persona reintenta despues de un fallo
  // (password incorrecto, etc.), ese segundo intento pide uno fresco.
  altchaChallengePrefetch: Promise<any> | null = null;
  private altchaChallengeFetchedAt = 0;

  prefetchAltchaChallenge(): void {
    this.altchaChallengeFetchedAt = Date.now();
    this.altchaChallengePrefetch = fetch(`${this.API}/auth/altcha-challenge`)
      .then(r => r.ok ? r.json() : null)
      .catch(() => null);
  }

  // El backend vence el desafio a los 5 min (ver AuthController::altchaChallenge,
  // expiresAt: time() + 300). Se usa 4 min aca (240s) como margen de
  // seguridad, no el limite exacto, para no arriesgarse a mandar uno que
  // vence a mitad del envio.
  private prefetchSigueVigente(): boolean {
    return this.altchaChallengePrefetch !== null
      && (Date.now() - this.altchaChallengeFetchedAt) < 240_000;
  }

  // Usado por login.component.ts al volver a la pestaña: solo pide un
  // desafio nuevo si de verdad hace falta (no hay ninguno pedido, el que
  // habia fallo, o ya esta por vencer) - si el que ya esta llego bien y
  // sigue vigente, no lo desperdicia pidiendo otro sin necesidad.
  async refrescarAltchaSiHaceFalta(): Promise<void> {
    if (this.prefetchSigueVigente()) {
      const actual = this.altchaChallengePrefetch;
      const resultado = await actual;
      // Si mientras se esperaba nadie lo reemplazo/consumio, y sí trajo
      // datos validos, no hace falta pedir otro.
      if (resultado && this.altchaChallengePrefetch === actual) return;
    }
    this.prefetchAltchaChallenge();
  }

  private tomarAltchaChallengePrefetch(): Promise<any> | null {
    const p = this.altchaChallengePrefetch;
    this.altchaChallengePrefetch = null;
    return p;
  }

  private async intentarResolverAltcha(): Promise<{ token: string; diag: string }> {
    // Si hay un desafio pedido de antemano (ver prefetchAltchaChallenge),
    // se usa ese en vez de salir a pedir uno nuevo - si fallo o no habia,
    // cae al fetch normal de siempre, con su mismo timeout de 8s.
    const prefetch = this.tomarAltchaChallengePrefetch();
    let ch: any = prefetch ? await prefetch : null;

    if (!ch) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);
      try {
        const resp = await fetch(`${this.API}/auth/altcha-challenge`, { signal: controller.signal });
        clearTimeout(timeoutId);
        if (!resp.ok) { return { token: '', diag: `fetch_status_${resp.status}` }; }
        ch = await resp.json();
      } catch (err: any) {
        clearTimeout(timeoutId);
        const diag = `${err?.name ?? 'Error'}: ${err?.message ?? 'sin mensaje'}`;
        return { token: '', diag };
      }
    }

    try {
      // El backend devuelve los parametros aplanados; solveChallenge espera la
      // estructura anidada { parameters: {...}, signature }.
      const challengeObj = {
        parameters: {
          algorithm: ch.algorithm,
          cost: ch.cost,
          keyLength: ch.keyLength,
          keyPrefix: ch.keyPrefix,
          nonce: ch.nonce,
          salt: ch.salt,
          ...(ch.expiresAt !== undefined ? { expiresAt: ch.expiresAt } : {}),
        },
        signature: ch.signature,
      };

      const solution = await solveChallenge({
        challenge: challengeObj as any,
        deriveKey: sha.deriveKey,
      });
      if (!solution) { return { token: '', diag: 'sin_solucion' }; }

      // Payload con la estructura que espera la libreria PHP del backend:
      // { challenge: { parameters, signature }, solution: { counter, derivedKey, time } }
      const payload = {
        challenge: challengeObj,
        solution: {
          counter: solution.counter,
          derivedKey: solution.derivedKey,
          ...(solution.time !== undefined ? { time: solution.time } : {}),
        },
      };

      return { token: btoa(JSON.stringify(payload)), diag: 'ok' };
    } catch (err: any) {
      const diag = `${err?.name ?? 'Error'}: ${err?.message ?? 'sin mensaje'}`;
      return { token: '', diag };
    }
  }

  // ── Mantener compatibilidad con otros módulos ─────────────────────────
  loginUsuario(nick: string, password: string): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${this.API}/auth/login`, { nick, password })
      .pipe(tap(res => this.guardarSesion(res)));
  }

  loginCodCliente(codCliente: string): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${this.API}/auth/login-cod-cliente`, { codCliente })
      .pipe(tap(res => this.guardarSesion(res)));
  }

  logout(expirada = false): void {
    // El candado solo aplica al logout AUTOMATICO por 401 (evita cascada de
    // multiples 401 simultaneos). El logout MANUAL del usuario siempre procede.
    if (expirada && this.cerrandoSesion) return;
    this.cerrandoSesion = true;
    // 1. Limpiar la sesion local primero (lo critico, no puede fallar)
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.DASHBOARD_KEY);
    sessionStorage.removeItem('dw_cartelera');
    sessionStorage.removeItem(this.JUNTA_KEY);
    // 2. Navegar a login de inmediato (garantizado, ya no depende del POST)
    this.router.navigate(['/login'], expirada ? { queryParams: { expirada: 1 } } : {});
    // 3. Avisar al backend best-effort; si falla (401 por token ya vencido) no rompe nada
    this.http.post(`${this.API}/auth/logout`, {}).subscribe({ error: () => {} });
  }

  // ── Getters ───────────────────────────────────────────────────────────
  getToken(): string | null       { return localStorage.getItem(this.TOKEN_KEY); }
  getDashboardCache(): any | null {
    const raw = localStorage.getItem(this.DASHBOARD_KEY);
    if (!raw) return null;
    try { return JSON.parse(raw); } catch { return null; }
  }
  clearDashboardCache(): void { localStorage.removeItem(this.DASHBOARD_KEY); }

  // ── Junta de Condominio: persiste solo durante la sesión actual ───────
  isJuntaDesbloqueada(): boolean {
    return sessionStorage.getItem(this.JUNTA_KEY) === '1';
  }

  marcarJuntaDesbloqueada(): void {
    sessionStorage.setItem(this.JUNTA_KEY, '1');
  }
  isAuthenticated(): boolean      { return !!this.getToken(); }

  getUser(): DanoralUser | null {
    const raw = localStorage.getItem(this.USER_KEY);
    return raw ? JSON.parse(raw) : null;
  }

  // Codigos que dan acceso al PANEL administrativo (no al portal de propietario).
  // Cualquiera de estos roles entra al panel admin; el menu luego filtra que ve cada uno.
  private readonly ACCESOS_ADMIN = [100001, 100003, 100011, 100012, 100013, 100014];

  isAdmin(): boolean {
    const accesos = this.getUser()?.accesos ?? [];
    return accesos.some(a => this.ACCESOS_ADMIN.includes(+a));
  }

  hasAcceso(codigo: Acceso | number): boolean {
    return this.getUser()?.accesos?.includes(+codigo) ?? false;
  }

  hasAnyAcceso(...codigos: (Acceso | number)[]): boolean {
    return codigos.some(c => this.hasAcceso(c));
  }

  // Prefetch de cartelera/encuestas: se disparan apenas hay token (ver
  // guardarSesion), sin esperar su respuesta. El dashboard los consume con
  // tomarCarteleraPrefetch()/tomarEncuestasPrefetch() cuando los necesita -
  // para ese momento puede que ya hayan terminado, o al menos ya llevan
  // parte del camino recorrido mientras Angular navegaba a la pantalla.
  carteleraPrefetch: Promise<any> | null = null;
  encuestasPrefetch: Promise<any> | null = null;

  private guardarSesion(res: AuthResponse): void {
    localStorage.setItem(this.TOKEN_KEY, res.token);
    localStorage.setItem(this.USER_KEY, JSON.stringify({ ...res.user, typeLogin: res.typeLogin }));
    if (res.dashboard) {
      localStorage.setItem(this.DASHBOARD_KEY, JSON.stringify(res.dashboard));
    }

    // Cartelera y encuestas ya no vienen dentro de la respuesta del login
    // (se movieron a endpoints propios, GET /cartelera y GET /encuestas,
    // para que el login deje de esperar por ellas). Se piden aca mismo, en
    // paralelo, apenas se tiene el token - pero SOLO para login de codigo
    // cliente (apartamento) - un usuario web/admin no tiene id_Entidad de
    // apartamento y esos endpoints le devuelven 403, sin sentido pedirlos.
    if (res.typeLogin === TYPE_LOGIN_CODCLIENT) {
      this.carteleraPrefetch = fetch(`${this.API}/cartelera`, {
        headers: { Authorization: `Bearer ${res.token}` },
      }).then(r => r.ok ? r.json() : null).catch(() => null);

      this.encuestasPrefetch = fetch(`${this.API}/encuestas`, {
        headers: { Authorization: `Bearer ${res.token}` },
      }).then(r => r.ok ? r.json() : null).catch(() => null);
    }
  }

  // El dashboard llama a esto UNA vez al montar - si venimos justo del
  // login, ya hay un prefetch en camino (o listo) y lo aprovecha. Si no
  // (recarga de pagina, navegacion directa), devuelve null y el dashboard
  // hace su propio fetch normal como respaldo.
  tomarCarteleraPrefetch(): Promise<any> | null {
    const p = this.carteleraPrefetch;
    this.carteleraPrefetch = null;
    return p;
  }

  tomarEncuestasPrefetch(): Promise<any> | null {
    const p = this.encuestasPrefetch;
    this.encuestasPrefetch = null;
    return p;
  }
}
